import { useAppStore } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';

/**
 * Right-click command completion helpers (BIM behavior).
 *
 * When the persisted preference `interactionOptions.rightClickCompletesCommand`
 * is on, a plain right-click during an active command finishes it — the exact
 * same path as pressing Enter (the active tool's `onKeyDown` receives a
 * synthetic keydown through the window listener in useToolController).
 * When idle, the same plain right-click re-arms the last-used tool
 * (2D-style repeat). Holding Shift always falls back to the classic
 * context menu.
 */

/** True while a non-select command/tool is running in the viewport. */
export function isCommandActive(): boolean {
    const tool = String(useAppStore.getState().activeTool ?? '');
    return tool.toLowerCase() !== 'select';
}

/**
 * Decide whether a right-click at the given modifier state should complete
 * the active command instead of opening the context menu.
 */
export function shouldRightClickComplete(shiftKey: boolean): boolean {
    if (shiftKey) return false;
    const state = useAppStore.getState();
    return state.interactionOptions.rightClickCompletesCommand && isCommandActive();
}

/**
 * Commit/finish the active command by replaying the Enter key through the
 * global key pipeline (tool `onKeyDown` handlers react exactly as if the user
 * pressed Enter). Tools that stay active afterwards — sketch tools that don't
 * consume Enter, or chained-wall mode — are exited back to Select, matching
 * BIM's right-click-to-finish behavior.
 */
export function finishActiveCommand(): void {
    window.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true })
    );
    if (isCommandActive()) {
        const appStore = useAppStore.getState();
        appStore.setActiveTool('select');
        appStore.clearSelection();
        useBIMStore.getState().setActiveTool('SELECT');
    }
}

/**
 * 2D-style repeat: when no command is active, a plain right-click
 * re-arms the last-used tool (store.lastUsedTool). This pairs with
 * finishActiveCommand — the first right-click completes the running command,
 * the next right-click calls it again. Shift always falls back to the classic
 * context menu. Returns true when a tool was re-activated, so the caller must
 * skip opening the context menu.
 */
export function tryRepeatLastCommand(shiftKey: boolean): boolean {
    if (shiftKey) return false;
    const state = useAppStore.getState();
    if (!state.interactionOptions.rightClickCompletesCommand) return false;
    if (isCommandActive()) return false;
    const last = state.lastUsedTool;
    if (!last || last === 'select') return false;
    state.setActiveTool(last);
    return true;
}
